// src/pages/qc/CcCreateModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { todayDubai } from "../../utils/dateUtils";

const TODAY = todayDubai();

const PRODUCT_TYPES = [
  { value: "WB",      label: "Water Based" },
  { value: "BF",      label: "Brake Fluid" },
  { value: "COOLANT", label: "Coolant" },
  { value: "HM",      label: "Hot Melt" },
];

export function CcCreateModal({ fgs, onSave, onCancel }) {
  const [form, setForm] = useState({
    customerName:  "",
    complaintDate: TODAY,
    productType:   "WB",
    productId:     "",
    productName:   "",
    batchNumber:   "",
    reportedBy:    "",
    complaint:     "",
    findings:      "",
  });
  const [products, setProducts] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Load product list when type changes
  useEffect(() => {
    setForm(f => ({ ...f, productId: "", productName: "" }));
    setProducts([]);

    async function load() {
      try {
        if (form.productType === "WB") {
          setProducts((fgs || []).map(f => ({ id: f.id, label: f.name || f.id })));
        } else if (form.productType === "BF") {
          const specs = await api.fetchBfSpecs();
          setProducts(specs.map(s => ({ id: s.productId, label: s.productId })));
        } else if (form.productType === "COOLANT") {
          const specs = await api.fetchCoolantSpecs();
          setProducts(specs.map(s => ({ id: s.productId, label: s.productId })));
        } else if (form.productType === "HM") {
          const grades = await api.fetchHmGrades();
          setProducts(grades.map(g => ({ id: g.id, label: (g.displayName || g.id) + (g.application ? ` — ${g.application}` : "") })));
        }
      } catch (e) {
        setError("Failed to load products: " + e.message);
      }
    }
    load();
  }, [form.productType, fgs]);

  function handleProductChange(e) {
    const id = e.target.value;
    const prod = products.find(p => p.id === id);
    setForm(f => ({ ...f, productId: id, productName: prod ? prod.label : id }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productId) { setError("Please select a product."); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        findings: form.findings || null,
      };
      await onSave(body);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal" style={{ width: 600 }}>
      <div className="modal-header">
        <div className="modal-title">New Customer Complaint</div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">

          {/* Product Type */}
          <div className="form-group">
            <label className="form-label">Product Type *</label>
            <div style={{ display: "flex", gap: 16 }}>
              {PRODUCT_TYPES.map(pt => (
                <label key={pt.value} style={{ display: "flex", alignItems: "center",
                  gap: 6, cursor: "pointer", fontWeight: form.productType === pt.value ? 700 : 400 }}>
                  <input type="radio" name="productType" value={pt.value}
                    checked={form.productType === pt.value}
                    onChange={() => set("productType", pt.value)} />
                  {pt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Product *</label>
              <select className="form-select" required value={form.productId}
                onChange={handleProductChange}>
                <option value="">— Select product —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Batch Number</label>
              <input className="form-input" value={form.batchNumber}
                onChange={e => set("batchNumber", e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Customer Name *</label>
              <input className="form-input" required value={form.customerName}
                onChange={e => set("customerName", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Complaint Date *</label>
              <input className="form-input" type="date" required value={form.complaintDate}
                onChange={e => set("complaintDate", e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Findings Reported By *</label>
            <input className="form-input" required value={form.reportedBy}
              onChange={e => set("reportedBy", e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Complaint *</label>
            <textarea className="form-input" rows={6} required value={form.complaint}
              onChange={e => set("complaint", e.target.value)}
              placeholder="Describe the complaint in detail…" />
          </div>

          <div className="form-group">
            <label className="form-label">Findings (including immediate causes)</label>
            <textarea className="form-input" rows={4} value={form.findings}
              onChange={e => set("findings", e.target.value)}
              placeholder="Any findings or immediate causes identified…" />
          </div>

          {error && <div className="alert-warn">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create Complaint"}
          </button>
        </div>
      </form>
    </div>
  );
}
